import { SocialService } from "./enums";

/**
 * Returns the public profile URL for a given social service and handle.
 * Handles all supported SocialService values.
 *
 * @example
 * getSocialProfileUrl("tiktok", "@user") // "https://www.tiktok.com/@user"
 * getSocialProfileUrl("instagram", "user") // "https://www.instagram.com/user"
 */
export const getSocialProfileUrl = (
  service: SocialService,
  handle: string,
): string => {
  const cleanHandle = handle.trim().replace(/^@+/, "");
  switch (service) {
    case "tiktok":
      return `https://www.tiktok.com/@${cleanHandle}`;
    case "instagram":
      return `https://www.instagram.com/${cleanHandle}`;
    case "youtube":
      return `https://www.youtube.com/@${cleanHandle}`;
    case "twitter":
      return `https://twitter.com/${cleanHandle}`;
    case "facebook":
      return `https://www.facebook.com/${cleanHandle}`;
    case "snapchat":
      return `https://www.snapchat.com/add/${cleanHandle}`;
    case "cameo":
      return `https://www.cameo.com/${cleanHandle}`;
    case "twitch":
      return `https://www.twitch.tv/${cleanHandle}`;
    default:
      return cleanHandle;
  }
};

const getAgeRangeLowerBound = (label: string): number => {
  const match = label.match(/\d+/);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
};

/**
 * Sorts audience age buckets youngest first, using the first number in each
 * label. Labels without a number (e.g. "Unknown") are placed last.
 *
 * @example
 * sortByAgeRange([{ label: "25-34" }, { label: "65+" }, { label: "18-24" }])
 * // [{ label: "18-24" }, { label: "25-34" }, { label: "65+" }]
 */
export const sortByAgeRange = <T extends { label: string }>(items: T[]): T[] =>
  [...items].sort(
    (a, b) => getAgeRangeLowerBound(a.label) - getAgeRangeLowerBound(b.label),
  );
